import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Users, Calendar, BookOpen, TrendingUp, UserPlus, Copy, Trash2, Plus, Mail, Phone, User, Clock, Eye, Trash, DollarSign, CreditCard, CheckCircle, Key, X, Package, Bell, AlertCircle, Info, AlertTriangle, CheckCircle2, Edit2, MessageSquare, Award, HelpCircle, ArrowUpRight, ArrowRight, Inbox, Upload, CalendarClock, Calculator } from 'lucide-react';
import api from '../utils/api';
import { formatHours } from '../utils/timeFormatting';
import FinancialDashboard from '../components/FinancialDashboard';
import ErrorBoundary from '../components/ErrorBoundary';
import AssignPackageModal from '../components/AssignPackageModal';
import TestimonialsManager from '../components/admin/TestimonialsManager';
import OutcomesManager from '../components/admin/OutcomesManager';
import FaqManager from '../components/admin/FaqManager';
import PagePreviewEditor from '../components/admin/PagePreviewEditor';
import { invalidateSiteContent } from '../hooks/useSiteContent';


interface Analytics {
  totalBookings: number;
  totalStudents: number;
  upcomingBookings: number;
  bookingsBySubject: Array<{
    subjectId: string;
    _count: { subjectId: number };
  }>;
  popularSubjects: Array<{
    name: string;
  }>;
}

interface InviteCode {
  id: string;
  code: string;
  studentName: string;
  phoneNumber?: string;
  email?: string;
  isUsed: boolean;
  usedAt?: string;
  createdAt: string;
}


interface Subject {
  id: string;
  name: string;
  category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
  onlineRate: number;
  inPersonRate: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Payment {
  id: string;
  amount: number;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  dueDate: string;
  paidAt?: string;
  createdAt: string;
  student: {
    name: string;
    email: string;
  };
  booking: {
    startTime: string;
    endTime: string;
    subject: {
      name: string;
      category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
    };
  };
}

interface HourPackage {
  id: string;
  totalHours: number;
  usedHours: number;
  remainingHours: number;
  packageType: string;
  purchasePrice: number;
  status: 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'DEPLETED' | 'REFUNDED';
  purchasedAt: string;
  student: {
    id: string;
    name: string;
    email: string;
  };
  subject: {
    id: string;
    name: string;
    category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
  };
}

interface PackageConfig {
  id: string;
  packageType: string;
  name: string;
  hours: number;
  discountPercent: number;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PackagePreviewExactLesson {
  transactionId: string;
  bookingId: string;
  title: string | null;
  type: 'LESSON' | 'CONSULTATION';
  status: string;
  startTime: string;
  endTime: string;
  actualHours: number;
  recordedHoursChanged: number;
}

interface PackagePreviewExactAdjustment {
  transactionId: string;
  hoursChanged: number;
  description: string | null;
  createdAt: string;
}

interface PackagePreviewExact {
  packageId: string;
  totalHours: number;
  stored: { usedHours: number; remainingHours: number };
  exact: {
    usedHoursFromLessons: number;
    adminAdjustmentHours: number;
    usedHours: number;
    remainingHours: number;
  };
  discrepancy: { usedHours: number; remainingHours: number };
  lessons: PackagePreviewExactLesson[];
  adminAdjustments: PackagePreviewExactAdjustment[];
}

interface PackageConfigCardProps {
  config: PackageConfig;
  onUpdate: (configId: string, patch: { name?: string; hours?: number; discountPercent?: number; isActive?: boolean }) => Promise<void> | void;
  onDelete: (configId: string, displayName: string) => Promise<void> | void;
}

interface Notice {
  id: string;
  title: string;
  content: string;
  type: 'INFO' | 'WARNING' | 'SUCCESS' | 'ERROR';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  isActive: boolean;
  targetRole?: 'STUDENT' | 'TUTOR' | null;
  expiresAt?: string;
  createdBy: string;
  createdAt: string;
  readCount?: number;
  totalStudents?: number;
  readPercentage?: number;
}

interface SiteContentItem {
  id: string;
  key: string;
  label: string;
  value: string;
  isVisible: boolean;
  section: string;
  sortOrder: number;
}

interface OverviewBooking {
  id: string;
  startTime: string;
  endTime: string;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'REQUESTED' | 'AMENDED' | 'DENIED';
  type: 'LESSON' | 'CONSULTATION';
  isOnline: boolean;
  title?: string | null;
  student?: { id: string; name: string } | null;
  subject?: { name: string } | null;
  createdAt: string;
}

interface ContactMessage {
  id: string;
  name: string;
  email?: string | null;
  message: string;
  isRead: boolean;
  createdAt: string;
}

const fallbackPackageName = (cfg: { name?: string; hours: number; packageType: string }): string => {
  if (cfg.name && cfg.name.trim().length > 0) return cfg.name;
  if (cfg.packageType === 'TEN_HOUR') return '10-Hour Package';
  if (cfg.packageType === 'TWENTY_HOUR') return '20-Hour Package';
  if (cfg.packageType === 'THIRTY_HOUR') return '30-Hour Package';
  return `${cfg.hours}-Hour Package`;
};

const PackageConfigCard: React.FC<PackageConfigCardProps> = ({ config, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(fallbackPackageName(config));
  const [hours, setHours] = useState(config.hours);
  const [discountPercent, setDiscountPercent] = useState(config.discountPercent);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayName = fallbackPackageName(config);

  const handleSave = async () => {
    if (!Number.isFinite(hours) || hours <= 0) {
      setError('Hours must be a positive number');
      return;
    }
    if (!Number.isFinite(discountPercent) || discountPercent < 0 || discountPercent > 100) {
      setError('Discount must be between 0 and 100');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onUpdate(config.id, {
        name: name.trim() || displayName,
        hours: Math.round(hours),
        discountPercent
      });
      setIsEditing(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setName(displayName);
    setHours(config.hours);
    setDiscountPercent(config.discountPercent);
    setError(null);
    setIsEditing(false);
  };

  const previewDiscount = isEditing ? discountPercent : config.discountPercent;
  const previewHours = isEditing ? hours : config.hours;

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
      <div className="flex justify-between items-start mb-4 gap-4">
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-2">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Starter Bundle"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={hours}
                  onChange={(e) => setHours(parseFloat(e.target.value) || 0)}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <span className="text-sm text-gray-600">hours</span>
              </div>
            </div>
          ) : (
            <>
              <h4 className="text-lg font-medium text-gray-900 mb-1">{displayName}</h4>
              <p className="text-sm text-gray-600">{config.hours} hours total</p>
            </>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {isEditing ? (
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(parseFloat(e.target.value) || 0)}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <span className="ml-1 text-sm text-gray-600">%</span>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="text-green-600 hover:text-green-700 p-1 disabled:opacity-50"
                title="Save changes"
              >
                <CheckCircle className="w-4 h-4" />
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="text-gray-600 hover:text-gray-700 p-1 disabled:opacity-50"
                title="Cancel changes"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="text-2xl font-bold text-primary-600">{config.discountPercent}%</div>
                <div className="text-sm text-gray-500">discount</div>
              </div>
              <button
                onClick={() => setIsEditing(true)}
                className="text-primary-600 hover:text-primary-700 p-1"
                title="Edit package"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(config.id, displayName)}
                className="text-red-600 hover:text-red-700 p-1"
                title="Delete package"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm">
        <div className="text-gray-600 mb-1">Example pricing (£25/hour rate):</div>
        <div className="flex justify-between">
          <span>Original price:</span>
          <span>£{(25 * previewHours).toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>Discount ({previewDiscount}%):</span>
          <span className="text-red-600">-£{((25 * previewHours) * previewDiscount / 100).toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-medium border-t border-gray-200 pt-1 mt-1">
          <span>Final price:</span>
          <span className="text-green-600">£{((25 * previewHours) * (1 - previewDiscount / 100)).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [hourPackages, setHourPackages] = useState<HourPackage[]>([]);
  const [packageConfigs, setPackageConfigs] = useState<PackageConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'invites' | 'subjects' | 'financials' | 'messages' | 'notices' | 'site-content' | 'testimonials' | 'outcomes' | 'faq'>('financials');
  const [contactMessages, setContactMessages] = useState<ContactMessage[]>([]);
  const [financialSubTab, setFinancialSubTab] = useState<'dashboard' | 'payments' | 'packages' | 'settings'>('dashboard');
  const [showAssignPackageModal, setShowAssignPackageModal] = useState(false);
  const [previewExactPkg, setPreviewExactPkg] = useState<HourPackage | null>(null);
  const [previewExactData, setPreviewExactData] = useState<PackagePreviewExact | null>(null);
  const [previewExactLoading, setPreviewExactLoading] = useState(false);
  const [previewExactError, setPreviewExactError] = useState<string | null>(null);

  // Global email kill switch (green = live, red = paused)
  const [emailsPaused, setEmailsPaused] = useState<boolean | null>(null);
  const [emailToggleLoading, setEmailToggleLoading] = useState(false);

  const handleOpenPreviewExact = async (pkg: HourPackage) => {
    setPreviewExactPkg(pkg);
    setPreviewExactData(null);
    setPreviewExactError(null);
    setPreviewExactLoading(true);
    try {
      const response = await api.get(`/hour-packages/${pkg.id}/preview-exact`);
      setPreviewExactData(response.data);
    } catch (err: any) {
      setPreviewExactError(err?.response?.data?.message || 'Failed to load preview');
    } finally {
      setPreviewExactLoading(false);
    }
  };

  const handleClosePreviewExact = () => {
    setPreviewExactPkg(null);
    setPreviewExactData(null);
    setPreviewExactError(null);
    setPreviewExactLoading(false);
  };
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({
    studentName: '',
    phoneNumber: '',
    email: ''
  });
  const [showSubjectForm, setShowSubjectForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [subjectFormData, setSubjectFormData] = useState({
    name: '',
    category: 'GCSE' as 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY',
    onlineRate: '',
    inPersonRate: '',
    isActive: true
  });
  const [notices, setNotices] = useState<Notice[]>([]);
  const [showNoticeForm, setShowNoticeForm] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  const [noticeFormData, setNoticeFormData] = useState({
    title: '',
    content: '',
    type: 'INFO' as 'INFO' | 'WARNING' | 'SUCCESS' | 'ERROR',
    priority: 'NORMAL' as 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT',
    targetRole: null as 'STUDENT' | 'TUTOR' | null,
    expiresAt: ''
  });
  const [noticeRecipients, setNoticeRecipients] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<Set<string>>(new Set());
  const [recipientFilter, setRecipientFilter] = useState('');
  const [siteContentItems, setSiteContentItems] = useState<SiteContentItem[]>([]);
  const [siteContentEdits, setSiteContentEdits] = useState<Record<string, { value: string; isVisible: boolean }>>({});
  const [savingSiteContent, setSavingSiteContent] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (user?.role === 'TUTOR') {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTab, financialSubTab]);

  // Load the current email on/off state once.
  useEffect(() => {
    if (user?.role !== 'TUTOR') return;
    api.get('/admin/email-pause')
      .then((res) => setEmailsPaused(!!res.data?.paused))
      .catch(() => setEmailsPaused(null));
  }, [user]);

  const toggleEmails = async () => {
    if (emailsPaused === null) return;
    const next = !emailsPaused;
    if (next && !window.confirm('Pause ALL outgoing emails? Nothing will be sent to students or parents until you turn this back on.')) {
      return;
    }
    setEmailToggleLoading(true);
    try {
      const res = await api.post('/admin/email-pause', { paused: next });
      setEmailsPaused(!!res.data?.paused);
    } catch (error) {
      window.alert('Failed to update email setting. Please try again.');
    } finally {
      setEmailToggleLoading(false);
    }
  };

  // Auto-refresh contact messages every 30 seconds while the tab is open.
  useEffect(() => {
    if (user?.role !== 'TUTOR' || activeTab !== 'messages') return;
    const interval = setInterval(() => {
      fetchContactMessages();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTab]);

  // Lazy-load the recipient list the first time the notice form opens.
  useEffect(() => {
    if (showNoticeForm && noticeRecipients.length === 0) {
      fetchNoticeRecipients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNoticeForm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Token is handled automatically by api instance
      const requests = [
        api.get('/admin/analytics')
          .catch(error => {
            console.error('Failed to fetch analytics:', error);
            return { data: null };
          })
      ];
      
      if (activeTab === 'invites') {
        requests.push(
          api.get('/invite')
            .catch(error => {
              console.error('Failed to fetch invite codes:', error);
              return { data: null };
            })
        );
      }
      
      if (activeTab === 'financials') {
        // Fetch financial data based on selected subtab
        if (financialSubTab === 'payments') {
          requests.push(
            api.get('/payments/pending')
              .catch(error => {
                console.error('Failed to fetch payments:', error);
                return { data: null };
              })
          );
        } else if (financialSubTab === 'packages') {
          requests.push(
            api.get('/hour-packages/admin/all')
              .then(response => {
                return response;
              })
              .catch(error => {
                console.error('Failed to fetch hour packages - Full error:', error);
                console.error('Error response:', error.response);
                if (error.response?.data?.message?.includes('table not found')) {
                  console.error('DATABASE ERROR: Hour packages table missing!');
                }
                return { data: null };
              })
          );
        } else if (financialSubTab === 'settings') {
          requests.push(
            api.get('/hour-packages/configs')
              .catch(error => {
                console.error('Failed to fetch package configs:', error);
                return { data: null };
              })
          );
        }
      }
      
      
      const responses = await Promise.all(requests);
      setAnalytics(responses[0].data || null);
      
      if (responses[1]) {
        if (activeTab === 'invites') {
          setInviteCodes(Array.isArray(responses[1].data) ? responses[1].data : []);
        } else if (activeTab === 'financials') {
          if (financialSubTab === 'payments') {
            setPayments(Array.isArray(responses[1].data) ? responses[1].data : []);
          } else if (financialSubTab === 'packages') {
            setHourPackages(Array.isArray(responses[1].data) ? responses[1].data : []);
          } else if (financialSubTab === 'settings') {
            setPackageConfigs(Array.isArray(responses[1].data) ? responses[1].data : []);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInviteCodes = async () => {
    try {
      const response = await api.get('/invite');
      setInviteCodes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch invite codes:', error);
    }
  };



  const fetchSubjects = async () => {
    try {
      const response = await api.get('/subjects?includeInactive=true');
      setSubjects(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
    }
  };

  const fetchContactMessages = async () => {
    try {
      const response = await api.get('/messages');
      setContactMessages(Array.isArray(response.data?.messages) ? response.data.messages : []);
    } catch (error) {
      console.error('Failed to fetch contact messages:', error);
    }
  };

  const handleMarkMessageRead = async (id: string) => {
    try {
      await api.patch(`/messages/${id}/read`);
      setContactMessages(prev => prev.map(m => m.id === id ? { ...m, isRead: true } : m));
    } catch (error: any) {
      window.alert(error.response?.data?.error || 'Failed to mark message as read');
    }
  };

  const fetchNotices = async () => {
    try {
      const response = await api.get('/notices/admin/all');
      setNotices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch notices:', error);
    }
  };

  const fetchNoticeRecipients = async () => {
    try {
      const response = await api.get('/admin/students');
      const students = Array.isArray(response.data) ? response.data : [];
      const list = students
        .map((s: any) => ({ id: s.id, name: s.name || '(no name)', email: s.email || '' }))
        .filter((s: { email: string }) => !!s.email)
        .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
      setNoticeRecipients(list);
    } catch (error) {
      console.error('Failed to fetch recipient list:', error);
    }
  };

  const fetchSiteContent = async () => {
    try {
      const response = await api.get('/site-content');
      const items: SiteContentItem[] = Array.isArray(response.data) ? response.data : [];
      setSiteContentItems(items);
      const edits: Record<string, { value: string; isVisible: boolean }> = {};
      items.forEach(item => {
        edits[item.key] = { value: item.value, isVisible: item.isVisible };
      });
      setSiteContentEdits(edits);
    } catch (error) {
      console.error('Failed to fetch site content:', error);
    }
  };

  const handleSaveSiteContentItem = async (key: string) => {
    setSavingSiteContent(prev => ({ ...prev, [key]: true }));
    try {
      await api.put(`/site-content/${key}`, siteContentEdits[key]);
      invalidateSiteContent();
      window.alert('Content saved successfully');
      fetchSiteContent();
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to save content');
    } finally {
      setSavingSiteContent(prev => ({ ...prev, [key]: false }));
    }
  };



  const handleCreateInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteFormData.studentName) {
      window.alert('Student name is required');
      return;
    }

    try {
      const response = await api.post('/invite', inviteFormData);
      setShowInviteForm(false);
      setInviteFormData({ studentName: '', phoneNumber: '', email: '' });
      fetchInviteCodes();
      
      // Copy invite URL to clipboard
      const inviteUrl = response.data.inviteUrl;
      await navigator.clipboard.writeText(inviteUrl);
      window.alert(`Invite code created and URL copied to clipboard!\nCode: ${response.data.inviteCode.code}`);
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to create invite code');
    }
  };

  const handleCopyInviteUrl = async (code: string) => {
    const inviteUrl = `${window.location.origin}/register?invite=${code}`;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      window.alert('Invite URL copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleDeleteInvite = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this invite code? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/invite/${id}`);
      fetchInviteCodes();
      window.alert('Invite code deleted successfully');
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to delete invite code');
    }
  };

  const handleConfirmPayment = async (packageId: string) => {
    if (!window.confirm('Confirm that payment has been received for this hour package?')) {
      return;
    }

    try {
      await api.post(`/hour-packages/${packageId}/confirm-payment`);
      // Refresh hour packages list
      const response = await api.get('/hour-packages/admin/all');
      setHourPackages(Array.isArray(response.data) ? response.data : []);
      window.alert('Payment confirmed and package activated successfully');
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to confirm payment');
    }
  };


  const handleCreateSubject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subjectFormData.name || !subjectFormData.onlineRate || !subjectFormData.inPersonRate) {
      window.alert('Please fill in all required fields');
      return;
    }

    try {
      await api.post('/subjects', subjectFormData);
      
      setShowSubjectForm(false);
      setSubjectFormData({
        name: '',
        category: 'GCSE',
        onlineRate: '',
        inPersonRate: '',
        isActive: true
      });
      fetchSubjects();
      window.alert('Subject created successfully');
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to create subject');
    }
  };

  const handleUpdateSubject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSubject) return;

    try {
      await api.put(`/subjects/${editingSubject.id}`, subjectFormData);
      
      setShowSubjectForm(false);
      setEditingSubject(null);
      setSubjectFormData({
        name: '',
        category: 'GCSE',
        onlineRate: '',
        inPersonRate: '',
        isActive: true
      });
      fetchSubjects();
      window.alert('Subject updated successfully');
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to update subject');
    }
  };

  const handleDeleteSubject = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this subject? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/subjects/${id}`);
      fetchSubjects();
      window.alert('Subject deleted successfully');
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to delete subject');
    }
  };

  const handleEditSubject = (subject: Subject) => {
    setEditingSubject(subject);
    setSubjectFormData({
      name: subject.name,
      category: subject.category,
      onlineRate: subject.onlineRate.toString(),
      inPersonRate: subject.inPersonRate.toString(),
      isActive: subject.isActive
    });
    setShowSubjectForm(true);
  };

  const toggleSubjectStatus = async (subject: Subject) => {
    try {
      await api.put(`/subjects/${subject.id}`, {
        isActive: !subject.isActive
      });
      fetchSubjects();
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to update subject status');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatSubjectCategory = (category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY') => {
    switch (category) {
      case 'ELEVEN_PLUS':
        return '11+';
      case 'GCSE':
        return 'GCSE';
      case 'A_LEVEL':
        return 'A-Level';
      case 'UNIVERSITY':
        return 'University';
      default:
        return category;
    }
  };

  const confirmPayment = async (paymentId: string) => {
    try {
      await api.put(`/payments/${paymentId}/confirm`);
      // Refresh payments after confirmation
      fetchData();
      window.alert('Payment confirmed successfully!');
    } catch (error: any) {
      console.error('Failed to confirm payment:', error);
      window.alert(error.response?.data?.message || 'Failed to confirm payment');
    }
  };

  const refreshPackageConfigs = async () => {
    const response = await api.get('/hour-packages/configs');
    setPackageConfigs(Array.isArray(response.data) ? response.data : []);
  };

  const handleUpdatePackageConfig = async (
    configId: string,
    patch: { name?: string; hours?: number; discountPercent?: number; isActive?: boolean }
  ) => {
    await api.put(`/hour-packages/configs/${configId}`, patch);
    await refreshPackageConfigs();
  };

  const handleDeletePackageConfig = async (configId: string, displayName: string) => {
    if (!window.confirm(`Delete "${displayName}"? Students will no longer be able to purchase this package. Existing purchased packages keep their data and are not affected.`)) {
      return;
    }
    try {
      await api.delete(`/hour-packages/configs/${configId}`);
      await refreshPackageConfigs();
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to delete package');
    }
  };

  const [newPkgName, setNewPkgName] = useState('');
  const [newPkgHours, setNewPkgHours] = useState('');
  const [newPkgDiscount, setNewPkgDiscount] = useState('');
  const [newPkgSaving, setNewPkgSaving] = useState(false);

  const handleCreatePackageConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    const hours = parseFloat(newPkgHours);
    const discount = parseFloat(newPkgDiscount);
    if (!Number.isFinite(hours) || hours <= 0) {
      window.alert('Hours must be a positive number');
      return;
    }
    if (!Number.isFinite(discount) || discount < 0 || discount > 100) {
      window.alert('Discount must be between 0 and 100');
      return;
    }
    setNewPkgSaving(true);
    try {
      await api.post('/hour-packages/configs', {
        name: newPkgName.trim() || undefined,
        hours,
        discountPercent: discount
      });
      setNewPkgName('');
      setNewPkgHours('');
      setNewPkgDiscount('');
      await refreshPackageConfigs();
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to create package');
    } finally {
      setNewPkgSaving(false);
    }
  };

  const handleResetPackageConfigs = async () => {
    if (!window.confirm('Reset all package configurations to default values? This will DELETE all custom packages and recreate the 10/20/30-hour defaults.')) {
      return;
    }

    try {
      await api.post('/hour-packages/configs/reset-defaults');
      await refreshPackageConfigs();
      window.alert('Package configurations reset to defaults successfully');
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to reset package configurations');
    }
  };

  const handleCreateNotice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noticeFormData.title || !noticeFormData.content) {
      window.alert('Please fill in title and content');
      return;
    }

    try {
      const recipientIds = Array.from(selectedRecipientIds);
      const data = {
        ...noticeFormData,
        targetRole: noticeFormData.targetRole || undefined,
        expiresAt: noticeFormData.expiresAt || undefined,
        emailRecipientIds: recipientIds.length > 0 ? recipientIds : undefined,
      };

      await api.post('/notices/admin/create', data);

      setShowNoticeForm(false);
      setNoticeFormData({
        title: '',
        content: '',
        type: 'INFO',
        priority: 'NORMAL',
        targetRole: null,
        expiresAt: ''
      });
      setSelectedRecipientIds(new Set());
      setRecipientFilter('');
      fetchNotices();
      const sentTo = recipientIds.length;
      window.alert(
        sentTo > 0
          ? `Notice created and emailed to ${sentTo} recipient${sentTo === 1 ? '' : 's'}.`
          : 'Notice created successfully',
      );
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to create notice');
    }
  };

  const handleUpdateNotice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingNotice) return;

    try {
      const data = {
        ...noticeFormData,
        targetRole: noticeFormData.targetRole || undefined,
        expiresAt: noticeFormData.expiresAt || undefined
      };

      await api.put(`/notices/admin/${editingNotice.id}`, data);

      setShowNoticeForm(false);
      setEditingNotice(null);
      setNoticeFormData({
        title: '',
        content: '',
        type: 'INFO',
        priority: 'NORMAL',
        targetRole: null,
        expiresAt: ''
      });
      fetchNotices();
      window.alert('Notice updated successfully');
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to update notice');
    }
  };

  const handleDeleteNotice = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) {
      return;
    }

    try {
      await api.delete(`/notices/admin/${id}`);
      fetchNotices();
      window.alert('Notice deleted successfully');
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to delete notice');
    }
  };

  const handleEditNotice = (notice: Notice) => {
    setEditingNotice(notice);
    setNoticeFormData({
      title: notice.title,
      content: notice.content,
      type: notice.type,
      priority: notice.priority,
      targetRole: notice.targetRole || null,
      expiresAt: notice.expiresAt ? new Date(notice.expiresAt).toISOString().slice(0, 16) : ''
    });
    setShowNoticeForm(true);
  };

  const toggleNoticeStatus = async (notice: Notice) => {
    try {
      await api.put(`/notices/admin/${notice.id}`, {
        isActive: !notice.isActive
      });
      fetchNotices();
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to update notice status');
    }
  };

  const handleDeleteHourPackage = async (packageId: string, studentName: string, totalHours: number) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete ${studentName}'s ${totalHours}-hour package and ALL related data including:\n\n• Financial records\n• Transaction history\n• Associated booking data\n\nThis action CANNOT be undone!\n\nAre you absolutely sure you want to proceed?`)) {
      return;
    }

    // Second confirmation for critical action
    if (!window.confirm('Final confirmation: Delete this hour package and ALL its financial data permanently?')) {
      return;
    }

    try {
      const response = await api.delete(`/hour-packages/${packageId}`);

      // Show detailed deletion summary
      const summary = response.data.deletionSummary;
      let summaryMessage = `Hour package deleted successfully!\n\nDeleted:\n• Package: ${summary.totalHours}-hour\n• Student: ${summary.student.name}\n• Subject: ${summary.subject.name}\n• Purchase Price: £${summary.purchasePrice}\n• Transactions: ${summary.transactionsDeleted}`;
      
      if (summary.associatedBookings > 0) {
        summaryMessage += `\n• Associated Bookings: ${summary.associatedBookings}`;
      }
      
      window.alert(summaryMessage);
      
      // Refresh hour packages list
      const packagesResponse = await api.get('/hour-packages/admin/all');
      setHourPackages(Array.isArray(packagesResponse.data) ? packagesResponse.data : []);
      
    } catch (error: any) {
      window.alert(error.response?.data?.message || 'Failed to delete hour package');
    }
  };


  if (user?.role !== 'TUTOR') {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Manage your tutoring business and view analytics
          </p>
        </div>

        {/* Global email kill switch: green = live, red = paused */}
        <button
          onClick={toggleEmails}
          disabled={emailsPaused === null || emailToggleLoading}
          title={
            emailsPaused === null
              ? 'Email status unavailable'
              : emailsPaused
                ? 'Emails are OFF — click to turn them back on'
                : 'Emails are LIVE — click to pause all outgoing emails'
          }
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
            emailsPaused === null
              ? 'bg-gray-400'
              : emailsPaused
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          <Mail className="w-4 h-4" />
          <span>
            {emailToggleLoading
              ? 'Saving…'
              : emailsPaused === null
                ? 'Emails: —'
                : emailsPaused
                  ? 'Emails: OFF'
                  : 'Emails: LIVE'}
          </span>
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('invites');
              if (!Array.isArray(inviteCodes) || inviteCodes.length === 0) {
                fetchInviteCodes();
              }
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'invites'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Invite Codes
          </button>
          <button
            onClick={() => {
              setActiveTab('subjects');
              if (!Array.isArray(subjects) || subjects.length === 0) {
                fetchSubjects();
              }
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'subjects'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            <span>Subjects</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('financials');
              setFinancialSubTab('dashboard');
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'financials'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <DollarSign className="w-4 h-4" />
            <span>Financials</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('messages');
              fetchContactMessages();
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'messages'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Inbox className="w-4 h-4" />
            <span>Messages</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('notices');
              if (!Array.isArray(notices) || notices.length === 0) {
                fetchNotices();
              }
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'notices'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Bell className="w-4 h-4" />
            <span>Notices</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('site-content');
              fetchSiteContent();
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'site-content'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Edit2 className="w-4 h-4" />
            <span>Site Content</span>
          </button>
          <button
            onClick={() => setActiveTab('testimonials')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'testimonials'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            <span>Testimonials</span>
          </button>
          <button
            onClick={() => setActiveTab('outcomes')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'outcomes'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Award className="w-4 h-4" />
            <span>Outcomes</span>
          </button>
          <button
            onClick={() => setActiveTab('faq')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'faq'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <HelpCircle className="w-4 h-4" />
            <span>FAQ</span>
          </button>
        </nav>
      </div>



      {activeTab === 'invites' && (
        <div>
          {/* Create Invite Form */}
          {showInviteForm && (
            <div className="card mb-6">
              <div className="card-header">
                <h2 className="card-title">Create Invite Code</h2>
              </div>
              <form onSubmit={handleCreateInvite} className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student Name *
                    </label>
                    <input
                      type="text"
                      value={inviteFormData.studentName}
                      onChange={(e) => setInviteFormData({ ...inviteFormData, studentName: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={inviteFormData.phoneNumber}
                      onChange={(e) => setInviteFormData({ ...inviteFormData, phoneNumber: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={inviteFormData.email}
                      onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button type="submit" className="btn btn-primary">
                    Create Invite Code
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowInviteForm(false)}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Invite Codes List */}
          <div className="card">
            <div className="card-header">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="card-title">Invite Codes</h2>
                  <p className="card-subtitle">Generate codes for new student registration</p>
                </div>
                <button
                  onClick={() => setShowInviteForm(true)}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Invite</span>
                </button>
              </div>
            </div>

            {!Array.isArray(inviteCodes) || inviteCodes.length === 0 ? (
              <div className="text-center py-8">
                <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No invite codes created yet.</p>
                <p className="text-sm text-gray-500 mt-1">
                  Create invite codes to help new students register with pre-filled information.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Array.isArray(inviteCodes) ? inviteCodes.map((invite) => (
                      <tr key={invite.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono font-bold text-gray-900">{invite.code}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{invite.studentName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {invite.email && <div>{invite.email}</div>}
                            {invite.phoneNumber && <div className="text-gray-500">{invite.phoneNumber}</div>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            invite.isUsed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {invite.isUsed ? 'Used' : 'Active'}
                          </span>
                          {invite.isUsed && invite.usedAt && (
                            <div className="text-xs text-gray-500 mt-1">
                              Used {formatDate(invite.usedAt)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(invite.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleCopyInviteUrl(invite.code)}
                              className="text-primary-600 hover:text-primary-900"
                              title="Copy invite URL"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteInvite(invite.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete invite code"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                          No invite codes found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}


      {activeTab === 'messages' && (
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">Contact Messages</h2>
              <p className="card-subtitle">Messages sent via the contact form (refreshes every 30 seconds)</p>
            </div>
          </div>

          {contactMessages.length === 0 ? (
            <div className="text-center py-8">
              <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No contact messages yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {contactMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`border rounded-lg p-4 ${msg.isRead ? 'border-gray-200 bg-white' : 'border-primary-200 bg-primary-50'}`}
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900">{msg.name}</span>
                        {msg.email && (
                          <a href={`mailto:${msg.email}`} className="text-sm text-primary-600 hover:underline">
                            {msg.email}
                          </a>
                        )}
                        {!msg.isRead && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{msg.message}</p>
                      <p className="text-xs text-gray-500 mt-2">{formatDateTime(msg.createdAt)}</p>
                    </div>
                    {!msg.isRead && (
                      <button
                        onClick={() => handleMarkMessageRead(msg.id)}
                        className="flex items-center text-sm text-primary-600 hover:text-primary-700 bg-white border border-primary-200 px-3 py-1 rounded-md flex-shrink-0"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {activeTab === 'subjects' && (
        <div>
          {/* Create/Edit Subject Form */}
          {showSubjectForm && (
            <div className="card mb-6">
              <div className="card-header">
                <h2 className="card-title">
                  {editingSubject ? 'Edit Subject' : 'Create New Subject'}
                </h2>
              </div>
              <form onSubmit={editingSubject ? handleUpdateSubject : handleCreateSubject} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject Name *
                    </label>
                    <input
                      type="text"
                      value={subjectFormData.name}
                      onChange={(e) => setSubjectFormData({ ...subjectFormData, name: e.target.value })}
                      className="input"
                      placeholder="e.g., Mathematics, English Literature"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={subjectFormData.category}
                      onChange={(e) => setSubjectFormData({ ...subjectFormData, category: e.target.value as 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY' })}
                      className="input"
                      required
                    >
                      <option value="ELEVEN_PLUS">11+ (Age 10-11)</option>
                      <option value="GCSE">GCSE (Age 14-16)</option>
                      <option value="A_LEVEL">A-Level (Age 16-18)</option>
                      <option value="UNIVERSITY">University / Oxbridge</option>
                    </select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Online Rate (£) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={subjectFormData.onlineRate}
                      onChange={(e) => setSubjectFormData({ ...subjectFormData, onlineRate: e.target.value })}
                      className="input"
                      placeholder="20.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      In-Person Rate (£) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={subjectFormData.inPersonRate}
                      onChange={(e) => setSubjectFormData({ ...subjectFormData, inPersonRate: e.target.value })}
                      className="input"
                      placeholder="30.00"
                      required
                    />
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={subjectFormData.isActive}
                    onChange={(e) => setSubjectFormData({ ...subjectFormData, isActive: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                    Subject is active and available for booking
                  </label>
                </div>
                <div className="flex space-x-3">
                  <button type="submit" className="btn btn-primary">
                    {editingSubject ? 'Update Subject' : 'Create Subject'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSubjectForm(false);
                      setEditingSubject(null);
                      setSubjectFormData({
                        name: '',
                        category: 'GCSE',
                        onlineRate: '',
                        inPersonRate: '',
                        isActive: true
                      });
                    }}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Subjects List */}
          <div className="card">
            <div className="card-header">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="card-title">Subject Management</h2>
                  <p className="card-subtitle">Manage available subjects, categories, and pricing</p>
                </div>
                <button
                  onClick={() => setShowSubjectForm(true)}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Subject</span>
                </button>
              </div>
            </div>

            {!Array.isArray(subjects) || subjects.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No subjects created yet.</p>
                <p className="text-sm text-gray-500 mt-1">
                  Create subjects to offer tutoring services across different age groups.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Group subjects by category */}
                {['ELEVEN_PLUS', 'GCSE', 'A_LEVEL', 'UNIVERSITY'].map((category) => {
                  const categorySubjects = subjects.filter(s => s.category === category);
                  if (categorySubjects.length === 0) return null;

                  const categoryName =
                    category === 'ELEVEN_PLUS' ? '11+ Subjects' :
                    category === 'GCSE' ? 'GCSE Subjects' :
                    category === 'A_LEVEL' ? 'A-Level Subjects' :
                    'University / Oxbridge Subjects';

                  return (
                    <div key={category}>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                        <BookOpen className="w-5 h-5 mr-2 text-primary-600" />
                        {categoryName} ({categorySubjects.length})
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Subject
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Online Rate
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                In-Person Rate
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {categorySubjects.map((subject) => (
                              <tr key={subject.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{subject.name}</div>
                                  <div className="text-sm text-gray-500">Created {formatDate(subject.createdAt)}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  £{subject.onlineRate.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  £{subject.inPersonRate.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <button
                                    onClick={() => toggleSubjectStatus(subject)}
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                                      subject.isActive
                                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                                    }`}
                                  >
                                    {subject.isActive ? 'Active' : 'Inactive'}
                                  </button>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={() => handleEditSubject(subject)}
                                      className="text-primary-600 hover:text-primary-900"
                                      title="Edit subject"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteSubject(subject.id)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete subject"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'financials' && (
        <div>
          {/* Financial Sub-tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setFinancialSubTab('dashboard')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  financialSubTab === 'dashboard'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4" />
                  <span>Dashboard</span>
                </span>
              </button>
              <button
                onClick={() => setFinancialSubTab('payments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  financialSubTab === 'payments'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center space-x-2">
                  <CreditCard className="w-4 h-4" />
                  <span>Payments</span>
                </span>
              </button>
              <button
                onClick={() => setFinancialSubTab('packages')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  financialSubTab === 'packages'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>Hour Packages</span>
                </span>
              </button>
              <button
                onClick={() => setFinancialSubTab('settings')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  financialSubTab === 'settings'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center space-x-2">
                  <Key className="w-4 h-4" />
                  <span>Package Settings</span>
                </span>
              </button>
            </nav>
          </div>

          {/* Financial Dashboard */}
          {financialSubTab === 'dashboard' && (
            <ErrorBoundary>
              <FinancialDashboard />
            </ErrorBoundary>
          )}

          {/* Payments Tab */}
          {financialSubTab === 'payments' && (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Management</h2>
            <p className="text-gray-600">
              Stripe automatically marks payments as paid when checkout completes. Use "Mark as Paid" only for offline or cash payments.
            </p>
          </div>

          {payments.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No pending payments.</p>
              <p className="text-sm text-gray-500 mt-1">
                Payment confirmations will appear here.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Pending Payment Confirmations ({payments.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lesson Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {payment.student.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {payment.student.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {payment.booking.subject.name} - {formatSubjectCategory(payment.booking.subject.category)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDateTime(payment.booking.startTime)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            £{payment.amount}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(payment.dueDate)}
                          </div>
                          {new Date(payment.dueDate) < new Date() && (
                            <div className="text-sm text-red-600 font-medium">
                              Overdue
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            payment.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                            payment.status === 'PAID' ? 'bg-green-100 text-green-800' :
                            payment.status === 'OVERDUE' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {payment.status === 'PENDING' && (
                            <button
                              onClick={() => confirmPayment(payment.id)}
                              className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1"
                              title="Use only for offline/cash payments. Stripe payments confirm automatically."
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span>Mark as Paid (Manual)</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
          )}

          {/* Hour Packages Tab */}
          {financialSubTab === 'packages' && (
        <div>
          <div className="mb-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Hour Package Management</h2>
                <p className="text-gray-600">
                  Manage student hour packages and confirm payments
                </p>
              </div>
              <button
                onClick={() => setShowAssignPackageModal(true)}
                className="btn bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
              >
                <Package className="w-4 h-4" />
                <span>Assign Package</span>
              </button>
            </div>
          </div>

          {hourPackages.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No hour packages purchased yet.</p>
              <p className="text-sm text-gray-500 mt-1">
                Student hour packages will appear here.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Hour Packages ({hourPackages.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Package Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Hours
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {hourPackages.map((pkg) => (
                      <tr key={pkg.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{pkg.student.name}</div>
                            <div className="text-sm text-gray-500">{pkg.student.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{pkg.subject.name}</div>
                            <div className="text-sm text-gray-500">
                              {pkg.subject.category.replace('_', ' ')}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {pkg.totalHours}h Package
                            </div>
                            <div className="text-sm text-gray-500">
                              Purchased {formatDate(pkg.purchasedAt)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {formatHours(pkg.remainingHours)} / {pkg.totalHours} hrs
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div 
                                className="bg-green-600 h-2 rounded-full" 
                                style={{ width: `${(pkg.remainingHours / pkg.totalHours) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            £{pkg.purchasePrice.toFixed(2)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            pkg.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                            pkg.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                            pkg.status === 'DEPLETED' ? 'bg-red-100 text-red-800' :
                            pkg.status === 'EXPIRED' ? 'bg-gray-100 text-gray-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {pkg.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {pkg.status === 'PENDING' && (
                              <button
                                onClick={() => handleConfirmPayment(pkg.id)}
                                className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1"
                              >
                                <CheckCircle className="w-4 h-4" />
                                <span>Confirm Payment</span>
                              </button>
                            )}
                            {pkg.status === 'ACTIVE' && (
                              <div className="text-green-600 flex items-center space-x-1">
                                <CheckCircle className="w-4 h-4" />
                                <span>Active</span>
                              </div>
                            )}
                            <button
                              onClick={() => handleOpenPreviewExact(pkg)}
                              className="bg-indigo-600 text-white px-3 py-1 rounded-md hover:bg-indigo-700 transition-colors flex items-center space-x-1"
                              title={`Preview what ${pkg.student.name}'s balance should be based on actual lesson durations (read-only)`}
                            >
                              <Calculator className="w-4 h-4" />
                              <span>Preview Exact</span>
                            </button>
                            {/* Delete button - always available for admin */}
                            <button
                              onClick={() => handleDeleteHourPackage(pkg.id, pkg.student.name, pkg.totalHours)}
                              className="bg-red-600 text-white px-3 py-1 rounded-md hover:bg-red-700 transition-colors flex items-center space-x-1"
                              title={`Delete ${pkg.student.name}'s ${pkg.totalHours}-hour package`}
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
          )}

          {/* Package Settings Tab */}
          {financialSubTab === 'settings' && (
        <div>
          <div className="mb-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Package Settings</h2>
                <p className="text-gray-600">
                  Configure hour packages — set the name, hours, and discount for each.
                </p>
              </div>
              <button
                onClick={handleResetPackageConfigs}
                className="btn bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md"
              >
                Reset to Defaults
              </button>
            </div>
          </div>

          {/* Add Package form */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary-600" />
              Add a new package
            </h3>
            <form onSubmit={handleCreatePackageConfig} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Display name (optional)</label>
                <input
                  type="text"
                  value={newPkgName}
                  onChange={(e) => setNewPkgName(e.target.value)}
                  placeholder="e.g. Starter Bundle"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Hours</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={newPkgHours}
                  onChange={(e) => setNewPkgHours(e.target.value)}
                  required
                  placeholder="50"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Discount %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={newPkgDiscount}
                  onChange={(e) => setNewPkgDiscount(e.target.value)}
                  required
                  placeholder="20"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="sm:col-span-4 flex justify-end">
                <button
                  type="submit"
                  disabled={newPkgSaving}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {newPkgSaving ? 'Adding…' : 'Add Package'}
                </button>
              </div>
            </form>
          </div>

          {packageConfigs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No packages configured yet.</p>
              <p className="text-sm text-gray-500 mt-1">
                Add one above, or click "Reset to Defaults" to create 10/20/30-hour starter packages.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Configured Packages ({packageConfigs.length})
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Changes apply immediately to new purchases. Existing purchased packages are not affected.
                </p>
              </div>
              <div className="p-6">
                <div className="grid gap-6">
                  {packageConfigs
                    .slice()
                    .sort((a, b) => (a.sortOrder - b.sortOrder) || (a.hours - b.hours))
                    .map((config) => (
                      <PackageConfigCard
                        key={config.id}
                        config={config}
                        onUpdate={handleUpdatePackageConfig}
                        onDelete={handleDeletePackageConfig}
                      />
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
          )}
        </div>
      )}


      {activeTab === 'notices' && (
        <div>
          {/* Create/Edit Notice Form */}
          {showNoticeForm && (
            <div className="card mb-6">
              <div className="card-header">
                <h2 className="card-title">
                  {editingNotice ? 'Edit Notice' : 'Create New Notice'}
                </h2>
              </div>
              <form onSubmit={editingNotice ? handleUpdateNotice : handleCreateNotice} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={noticeFormData.title}
                      onChange={(e) => setNoticeFormData({ ...noticeFormData, title: e.target.value })}
                      className="input"
                      placeholder="Important Announcement"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Audience
                    </label>
                    <select
                      value={noticeFormData.targetRole || ''}
                      onChange={(e) => setNoticeFormData({
                        ...noticeFormData,
                        targetRole: e.target.value as 'STUDENT' | 'TUTOR' | null || null
                      })}
                      className="input"
                    >
                      <option value="">All Users</option>
                      <option value="STUDENT">Students Only</option>
                      <option value="TUTOR">Tutors Only</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content *
                  </label>
                  <textarea
                    value={noticeFormData.content}
                    onChange={(e) => setNoticeFormData({ ...noticeFormData, content: e.target.value })}
                    className="input min-h-[100px]"
                    placeholder="Enter your notice content here..."
                    required
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      value={noticeFormData.type}
                      onChange={(e) => setNoticeFormData({
                        ...noticeFormData,
                        type: e.target.value as 'INFO' | 'WARNING' | 'SUCCESS' | 'ERROR'
                      })}
                      className="input"
                    >
                      <option value="INFO">Info</option>
                      <option value="SUCCESS">Success</option>
                      <option value="WARNING">Warning</option>
                      <option value="ERROR">Error</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority
                    </label>
                    <select
                      value={noticeFormData.priority}
                      onChange={(e) => setNoticeFormData({
                        ...noticeFormData,
                        priority: e.target.value as 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'
                      })}
                      className="input"
                    >
                      <option value="LOW">Low</option>
                      <option value="NORMAL">Normal</option>
                      <option value="HIGH">High</option>
                      <option value="URGENT">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expires At (Optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={noticeFormData.expiresAt}
                      onChange={(e) => setNoticeFormData({ ...noticeFormData, expiresAt: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>

                {!editingNotice && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Also email this notice to:</h3>
                        <p className="text-xs text-gray-600">
                          Tick recipients below. Leave empty to skip emails (notice still appears on the website).
                        </p>
                      </div>
                      <div className="text-xs text-gray-700 font-medium">
                        {selectedRecipientIds.size} selected
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="text"
                        value={recipientFilter}
                        onChange={(e) => setRecipientFilter(e.target.value)}
                        placeholder="Filter by name or email…"
                        className="input flex-1 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setSelectedRecipientIds(new Set(noticeRecipients.map(r => r.id)))}
                        className="text-xs text-primary-600 hover:text-primary-700 whitespace-nowrap"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedRecipientIds(new Set())}
                        className="text-xs text-gray-600 hover:text-gray-900 whitespace-nowrap"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-md">
                      {noticeRecipients.length === 0 ? (
                        <p className="text-sm text-gray-500 p-3">Loading recipients…</p>
                      ) : (
                        noticeRecipients
                          .filter(r => {
                            if (!recipientFilter.trim()) return true;
                            const q = recipientFilter.trim().toLowerCase();
                            return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
                          })
                          .map(r => {
                            const checked = selectedRecipientIds.has(r.id);
                            return (
                              <label
                                key={r.id}
                                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    const next = new Set(selectedRecipientIds);
                                    if (checked) next.delete(r.id);
                                    else next.add(r.id);
                                    setSelectedRecipientIds(next);
                                  }}
                                  className="h-4 w-4"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate">{r.name}</div>
                                  <div className="text-xs text-gray-500 truncate">{r.email}</div>
                                </div>
                              </label>
                            );
                          })
                      )}
                    </div>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button type="submit" className="btn btn-primary">
                    {editingNotice ? 'Update Notice' : 'Create Notice'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNoticeForm(false);
                      setEditingNotice(null);
                      setNoticeFormData({
                        title: '',
                        content: '',
                        type: 'INFO',
                        priority: 'NORMAL',
                        targetRole: null,
                        expiresAt: ''
                      });
                      setSelectedRecipientIds(new Set());
                      setRecipientFilter('');
                    }}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Notices List */}
          <div className="card">
            <div className="card-header">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="card-title">Notice Management</h2>
                  <p className="card-subtitle">Create and manage notices for students</p>
                </div>
                <button
                  onClick={() => setShowNoticeForm(true)}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Notice</span>
                </button>
              </div>
            </div>

            {!Array.isArray(notices) || notices.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No notices created yet.</p>
                <p className="text-sm text-gray-500 mt-1">
                  Create notices to broadcast important information to students.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {notices.map((notice) => {
                  const getNoticeIcon = () => {
                    switch (notice.type) {
                      case 'INFO': return <Info className="w-5 h-5" />;
                      case 'WARNING': return <AlertTriangle className="w-5 h-5" />;
                      case 'SUCCESS': return <CheckCircle2 className="w-5 h-5" />;
                      case 'ERROR': return <AlertCircle className="w-5 h-5" />;
                      default: return <Info className="w-5 h-5" />;
                    }
                  };

                  const getNoticeColor = () => {
                    switch (notice.type) {
                      case 'INFO': return 'bg-blue-50 border-blue-200 text-blue-800';
                      case 'WARNING': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
                      case 'SUCCESS': return 'bg-green-50 border-green-200 text-green-800';
                      case 'ERROR': return 'bg-red-50 border-red-200 text-red-800';
                      default: return 'bg-gray-50 border-gray-200 text-gray-800';
                    }
                  };

                  const getPriorityBadge = () => {
                    const colors = {
                      LOW: 'bg-gray-100 text-gray-600',
                      NORMAL: 'bg-blue-100 text-blue-600',
                      HIGH: 'bg-orange-100 text-orange-600',
                      URGENT: 'bg-red-100 text-red-600'
                    };
                    return colors[notice.priority] || colors.NORMAL;
                  };

                  return (
                    <div
                      key={notice.id}
                      className={`border rounded-lg p-4 ${getNoticeColor()}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-start space-x-3">
                          <div className="mt-1">{getNoticeIcon()}</div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="font-semibold text-gray-900">{notice.title}</h3>
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityBadge()}`}>
                                {notice.priority}
                              </span>
                              {notice.targetRole && (
                                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-600">
                                  {notice.targetRole}S ONLY
                                </span>
                              )}
                              {!notice.isActive && (
                                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                  INACTIVE
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{notice.content}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-600">
                              <span>Created by {notice.createdBy}</span>
                              <span>{formatDateTime(notice.createdAt)}</span>
                              {notice.expiresAt && (
                                <span className="text-orange-600">
                                  Expires: {formatDateTime(notice.expiresAt)}
                                </span>
                              )}
                              {notice.totalStudents !== undefined && notice.totalStudents > 0 && (
                                <span className="text-green-600">
                                  Read by {notice.readCount}/{notice.totalStudents} ({notice.readPercentage}%)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => toggleNoticeStatus(notice)}
                            className={`px-3 py-1 rounded text-xs font-medium ${
                              notice.isActive
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {notice.isActive ? 'Active' : 'Inactive'}
                          </button>
                          <button
                            onClick={() => handleEditNotice(notice)}
                            className="text-primary-600 hover:text-primary-700 p-1"
                            title="Edit notice"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteNotice(notice.id)}
                            className="text-red-600 hover:text-red-700 p-1"
                            title="Delete notice"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'site-content' && (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Site Content</h2>
            <p className="text-gray-600">
              Edit the text that appears on each public page. Each section is rendered below roughly as it looks on the live site — click into any field, make your changes, and hit "Save section".
            </p>
          </div>

          {/* Visual preview editor: sees each string in context on a mock-up
              of the public page it lives on. Replaces the flat per-key list. */}
          <ErrorBoundary>
            <PagePreviewEditor />
          </ErrorBoundary>

          {/* Fallback for anything the preview editor doesn't cover — SEO meta,
              retired/legacy keys, anything without a section. The tutor can
              still edit them here one key at a time. */}
          {siteContentItems.length > 0 && (() => {
            const PAGE_SECTIONS = new Set([
              'Home - Hero',
              'Home - Stats',
              'Home - About',
              'Home - How It Works',
              'Home - Testimonials',
              'Home - Outcomes',
              'Home - FAQ',
              'About Page',
              'Subjects Page',
              'Oxbridge Page',
              'Rates Page',
              'Contact Page',
              'Footer',
            ]);
            const otherItems = siteContentItems
              .filter(item => !PAGE_SECTIONS.has(item.section))
              .sort((a, b) => {
                if (a.section !== b.section) return a.section.localeCompare(b.section);
                if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
                return a.key.localeCompare(b.key);
              });

            if (otherItems.length === 0) return null;

            return (
              <details className="mt-12 border-t border-rule pt-6">
                <summary className="cursor-pointer text-sm text-muted hover:text-ink">
                  Show advanced ({otherItems.length} keys: SEO, legacy, uncategorised) — flat list
                </summary>
                <div className="mt-6 space-y-4">
                  {otherItems.map(item => {
                    const current = siteContentEdits[item.key]?.value ?? item.value;
                    const isLong = current.length > 80 || current.includes('\n');
                    return (
                      <div key={item.key} className="card">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div>
                            <label className="block text-sm font-medium text-ink mb-1">
                              {item.label}
                            </label>
                            <code className="text-xs text-muted">{item.key} · {item.section || 'uncategorised'}</code>
                          </div>
                          <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={siteContentEdits[item.key]?.isVisible ?? item.isVisible}
                              onChange={e => setSiteContentEdits(prev => ({
                                ...prev,
                                [item.key]: {
                                  value: prev[item.key]?.value ?? item.value,
                                  isVisible: e.target.checked,
                                },
                              }))}
                            />
                            <span>Visible</span>
                          </label>
                        </div>
                        <textarea
                          className={`input resize-y ${isLong ? 'min-h-[120px]' : 'min-h-[44px]'}`}
                          rows={isLong ? 5 : 2}
                          value={current}
                          onChange={e => setSiteContentEdits(prev => ({
                            ...prev,
                            [item.key]: {
                              value: e.target.value,
                              isVisible: prev[item.key]?.isVisible ?? item.isVisible,
                            },
                          }))}
                        />
                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={() => handleSaveSiteContentItem(item.key)}
                            disabled={savingSiteContent[item.key]}
                            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {savingSiteContent[item.key] ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            );
          })()}
        </div>
      )}

      {activeTab === 'testimonials' && (
        <ErrorBoundary>
          <TestimonialsManager />
        </ErrorBoundary>
      )}

      {activeTab === 'outcomes' && (
        <ErrorBoundary>
          <OutcomesManager />
        </ErrorBoundary>
      )}

      {activeTab === 'faq' && (
        <ErrorBoundary>
          <FaqManager />
        </ErrorBoundary>
      )}

      {/* Preview Exact Modal (read-only comparison) */}
      {previewExactPkg && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleClosePreviewExact}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-indigo-600" />
                  Preview Exact Balance
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {previewExactPkg.student.name} &middot; {previewExactPkg.subject.name} &middot;{' '}
                  {previewExactPkg.totalHours}h Package
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Read-only. Nothing is saved — this just shows what the balance should be based on
                  actual lesson durations.
                </p>
              </div>
              <button
                onClick={handleClosePreviewExact}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto">
              {previewExactLoading && (
                <div className="text-sm text-gray-500">Calculating…</div>
              )}
              {previewExactError && (
                <div className="text-sm text-red-600">{previewExactError}</div>
              )}
              {previewExactData && (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                        Stored (current)
                      </div>
                      <div className="text-2xl font-semibold text-gray-900">
                        {previewExactData.stored.remainingHours.toFixed(2)} hrs
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        remaining of {previewExactData.totalHours} hrs
                      </div>
                      <div className="text-sm text-gray-500">
                        Used: {previewExactData.stored.usedHours.toFixed(2)} hrs
                      </div>
                    </div>
                    <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-4">
                      <div className="text-xs uppercase tracking-wide text-indigo-600 mb-1">
                        Exact (from lesson times)
                      </div>
                      <div className="text-2xl font-semibold text-indigo-900">
                        {previewExactData.exact.remainingHours.toFixed(2)} hrs
                      </div>
                      <div className="text-sm text-indigo-700 mt-1">
                        remaining of {previewExactData.totalHours} hrs
                      </div>
                      <div className="text-sm text-indigo-700">
                        Used: {previewExactData.exact.usedHours.toFixed(2)} hrs
                        {previewExactData.exact.adminAdjustmentHours !== 0 && (
                          <> (incl. {(-previewExactData.exact.adminAdjustmentHours).toFixed(2)} hrs from admin adjustments)</>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-6 p-3 rounded-md border text-sm flex items-start gap-2"
                       style={previewExactData.discrepancy.remainingHours === 0
                         ? { borderColor: '#bbf7d0', backgroundColor: '#f0fdf4', color: '#166534' }
                         : { borderColor: '#fde68a', backgroundColor: '#fffbeb', color: '#92400e' }}>
                    {previewExactData.discrepancy.remainingHours === 0 ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>Stored balance matches the exact calculation. No discrepancy.</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>
                          Discrepancy: stored remaining differs from exact by{' '}
                          <strong>{previewExactData.discrepancy.remainingHours > 0 ? '+' : ''}
                          {previewExactData.discrepancy.remainingHours.toFixed(2)} hrs</strong>.
                          (Exact remaining is {previewExactData.discrepancy.remainingHours > 0 ? 'higher' : 'lower'}.)
                        </span>
                      </>
                    )}
                  </div>

                  <div className="mb-2 text-sm font-medium text-gray-900">
                    Lessons booked against this package ({previewExactData.lessons.length})
                  </div>
                  {previewExactData.lessons.length === 0 ? (
                    <div className="text-sm text-gray-500 mb-4">No lessons recorded against this package.</div>
                  ) : (
                    <div className="overflow-x-auto mb-4">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">When</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actual hrs</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Recorded</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {previewExactData.lessons.map((l) => {
                            const recordedUsed = -l.recordedHoursChanged;
                            const mismatch = Math.abs(recordedUsed - l.actualHours) > 0.005;
                            return (
                              <tr key={l.transactionId}>
                                <td className="px-3 py-2 text-gray-900">
                                  {new Date(l.startTime).toLocaleString('en-GB', {
                                    day: '2-digit', month: 'short', year: 'numeric',
                                    hour: '2-digit', minute: '2-digit'
                                  })}
                                </td>
                                <td className="px-3 py-2 text-gray-600">{l.status}</td>
                                <td className="px-3 py-2 text-right text-gray-900">{l.actualHours.toFixed(2)}</td>
                                <td className={`px-3 py-2 text-right ${mismatch ? 'text-amber-700 font-medium' : 'text-gray-600'}`}>
                                  {recordedUsed.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {previewExactData.adminAdjustments.length > 0 && (
                    <>
                      <div className="mb-2 text-sm font-medium text-gray-900">
                        Admin adjustments ({previewExactData.adminAdjustments.length})
                      </div>
                      <div className="overflow-x-auto mb-2">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {previewExactData.adminAdjustments.map((a) => (
                              <tr key={a.transactionId}>
                                <td className="px-3 py-2 text-gray-900">
                                  {new Date(a.createdAt).toLocaleDateString('en-GB')}
                                </td>
                                <td className="px-3 py-2 text-gray-600">{a.description || '—'}</td>
                                <td className={`px-3 py-2 text-right ${a.hoursChanged < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  {a.hoursChanged > 0 ? '+' : ''}{a.hoursChanged.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={handleClosePreviewExact}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Package Modal */}
      <AssignPackageModal
        isOpen={showAssignPackageModal}
        onClose={() => setShowAssignPackageModal(false)}
        onSuccess={async () => {
          // Refresh hour packages list after successful assignment
          try {
            const response = await api.get('/hour-packages/admin/all');
            setHourPackages(Array.isArray(response.data) ? response.data : []);
            window.alert('Package assigned successfully!');
          } catch (error) {
            console.error('Failed to refresh packages:', error);
          }
        }}
      />
    </div>
  );
};

export default AdminPage; 